import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { navigateExternal } from '@/lib/navigate-external';
import { notifySuccess } from '@/lib/toast';
import { formatDate, toDate } from '@/lib/date';
import {
  getBillingSummary,
  getCapabilities,
  getSubscription,
  listPlans,
  createCheckout,
  createCustomerPortalSession,
  previewUpgrade,
  upgradeSubscription,
  redeemCode,
  extendTrial,
  runLifecycleAction,
  validateCheckoutCode,
  getChangelog,
  getInvoices,
  type ChangelogResponse,
  type ValidateCheckoutCodeResponse,
  type LifecycleAction,
  type LifecycleResponse,
  type PauseDurationMonths,
  type TrialExtensionResponse,
  type BillingSummary,
  type BillingPlan,
  type CheckoutResponse,
  type CustomerPortalResponse,
  type Subscription,
  type PreviewResponse,
  type UpgradeResponse,
  type RedeemCodeResponse,
  type Invoice,
} from '@/lib/billing';

/**
 * Hierarchical query keys for the billing surface. We only consume `summary()`
 * from the sidebar today, but the factory shape mirrors `profileKeys` /
 * `orgKeys` / `workspaceKeys` so future read endpoints (invoices, plan list,
 * usage history) slot in cleanly.
 */
/** Changelog rows per request. Matches the backend's own default of 20. */
const CHANGELOG_PAGE_SIZE = 20;


export const billingKeys = {
  all: ['billing'] as const,
  summary: () => [...billingKeys.all, 'summary'] as const,
  subscription: () => [...billingKeys.all, 'subscription'] as const,
  capabilities: () => [...billingKeys.all, 'capabilities'] as const,
  plans: () => [...billingKeys.all, 'plans'] as const,
  // The promo code is PART of the key, not a query param the fetcher hides: the
  // discounted and undiscounted quotes are different answers to different
  // questions. Keying on it makes "Apply" a refetch and "Remove code" an instant
  // cache hit on the quote we already have. `null` = no code.
  preview: (planId: number, code: string | null = null) =>
    [...billingKeys.all, 'preview', planId, code] as const,
  /**
   * Paddle's geo-resolved `PricePreview` for a BATCH of price ids. All three
   * parts are cache-defining, for the same reason `preview` keys on its code:
   * they are different questions, not query params a fetcher can hide.
   *   - `priceIds` — one entry per grid, sorted by the caller so catalog order
   *     can't fork the cache into two entries for one answer.
   *   - `customerId` — a known Paddle customer inherits their LOCKED currency,
   *     which can legitimately differ from what their IP would resolve to.
   *   - `discountId` — the checkout dialog re-previews a single price WITH a
   *     validated code; `null` = the plain list quote.
   */
  pricePreview: (
    priceIds: readonly string[],
    customerId: string | null,
    discountId: string | null = null,
  ) => [...billingKeys.all, 'price-preview', priceIds, customerId, discountId] as const,
  invoices: () => [...billingKeys.all, 'invoices'] as const,
  changelog: () => [...billingKeys.all, 'changelog'] as const,
  customerPortal: () => [...billingKeys.all, 'customer-portal'] as const,
};

/**
 * Trial counter only ticks once per day; staleness measured in minutes is
 * fine. Keeping a single-minute staleTime avoids hammering /billing/summary
 * on every sidebar mount while still letting an explicit invalidate (e.g.
 * after a successful checkout) refresh the card.
 */
export function useBillingSummary() {
  return useQuery<BillingSummary>({
    queryKey: billingKeys.summary(),
    queryFn: getBillingSummary,
    staleTime: 60_000,
  });
}

/**
 * Current subscription detail — per-org price, renewal/expiry, and effective
 * limits. `GET /billing/subscription` is readable by any workspace member; the
 * OWNER-only pieces of the page (manage subscription, promo code) are gated
 * separately in `SubscriptionDetailsCard`, not on this query. Shares the 60s
 * staleness of the summary card since the two read the same aggregate; an
 * explicit invalidate after a checkout refreshes both.
 */
export function useSubscription(options?: {
  enabled?: boolean;
  /**
   * When set, poll `GET /billing/subscription` every 2s until the returned
   * plan's id matches this value, then stop. Drives the P2 upgrade "processing"
   * state: a `PATCH /billing/subscription` lands asynchronously via the Paddle
   * webhook, so the card polls until the aggregate reflects the new plan.
   * Undefined/null → no polling (the default for the read-only details card).
   */
  pollUntilPlanId?: number | null;
  /**
   * Absolute epoch-ms deadline for the poll. Once reached, polling stops even if
   * the plan hasn't flipped — a bail-out for a webhook that never lands (F2), so
   * the "Applying…" state can't spin forever. Undefined/null → no deadline.
   */
  pollDeadlineAt?: number | null;
  /**
   * Same idea as `pollUntilPlanId`, but for the lifecycle flows (cancel / pause /
   * resume): those endpoints change no local state and are applied by the
   * `subscription.canceled/paused/resumed` webhook, so the card polls until
   * `status` reaches the expected value. Undefined/null → no status polling.
   *
   * Independent of `pollUntilPlanId` — only one is ever set at a time (a plan
   * change and a lifecycle action can't both be in flight from one card), and
   * either one being satisfied stops the interval.
   */
  pollUntilStatus?: Subscription['status'] | null;
  /**
   * The third poll target, for `revoke-scheduled-change`. That endpoint changes
   * NO status and NO plan — it clears the pending `scheduledChange` mirror via a
   * `subscription.updated` webhook — so neither target above can express it.
   * `true` → poll until `scheduledChange` is null. Shares the same
   * `pollDeadlineAt` bail-out as the other two.
   */
  pollUntilNoScheduledChange?: boolean;
}) {
  const pollUntilPlanId = options?.pollUntilPlanId ?? null;
  const pollDeadlineAt = options?.pollDeadlineAt ?? null;
  const pollUntilStatus = options?.pollUntilStatus ?? null;
  const pollUntilNoScheduledChange = options?.pollUntilNoScheduledChange ?? false;
  return useQuery<Subscription>({
    queryKey: billingKeys.subscription(),
    queryFn: getSubscription,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
    // While polling for a webhook-driven plan flip, a window-focus refetch could
    // race the poll and briefly re-derive a stale plan — keep the poll the sole
    // driver (F11).
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      // No target set → not polling at all (the default read-only card).
      if (pollUntilPlanId == null && pollUntilStatus == null && !pollUntilNoScheduledChange) {
        return false;
      }
      // Whichever target is set, reaching it stops the poll.
      if (pollUntilPlanId != null && query.state.data?.plan.id === pollUntilPlanId) return false;
      if (pollUntilStatus != null && query.state.data?.status === pollUntilStatus) return false;
      if (pollUntilNoScheduledChange && query.state.data?.scheduledChange == null) return false;
      // Cap the poll at the deadline — stop the interval so a webhook that never
      // lands doesn't leave us polling indefinitely.
      if (pollDeadlineAt != null && Date.now() >= pollDeadlineAt) return false;
      return 2000;
    },
  });
}

/**
 * Resource-access capabilities for the current plan. Capabilities only change
 * on a plan change (rare), so a 5-minute staleTime keeps the read cheap while
 * still picking up an explicit invalidate after a checkout / plan switch.
 *
 * `enabled` (mirroring `usePlansQuery` / `useInvoices`) lets a caller that
 * already knows the answer skip the fetch entirely — `useFeatureLock` passes
 * `false` for the warmup sections, whose lock booleans arrive resolved on
 * `GET /email-accounts/:id/warmup-settings`.
 */
export function useCapabilities(options?: { enabled?: boolean }) {
  return useQuery<string[]>({
    queryKey: billingKeys.capabilities(),
    queryFn: getCapabilities,
    staleTime: 5 * 60_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Plan catalog for the self-serve Upgrade surface. The catalog is reference
 * data — the set of purchasable plans doesn't change per user — so it's cached
 * with an effectively infinite staleTime (the user's *current* plan lives on
 * `useBillingSummary`, which refreshes after a webhook-driven plan change).
 * `enabled` lets the caller skip the fetch for non-owners (the surface is
 * OWNER-only).
 */
export function usePlansQuery(options?: { enabled?: boolean }) {
  return useQuery<BillingPlan[]>({
    queryKey: billingKeys.plans(),
    queryFn: listPlans,
    staleTime: Infinity,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Payment / invoice history — the org's 5 most recent only; the API makes that
 * cut, not the client. `GET /billing/invoices` is readable by any
 * workspace member. Historical records that only change when a new payment
 * lands, so the 60s staleness shared by the other billing detail reads is fine;
 * an explicit invalidate of `billingKeys.all` after a webhook-driven change also
 * refreshes this list. The consuming card renders its own inline error/retry, so
 * (like `useSubscription`/`usePlansQuery`) no `silentError` is needed — the query
 * cache only auto-toasts background-refetch failures, not the first-load error.
 */
export function useInvoices(options?: { enabled?: boolean }) {
  return useQuery<Invoice[]>({
    queryKey: billingKeys.invoices(),
    queryFn: getInvoices,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Validate a promo code against the plan being purchased (OWNER-only).
 *
 * A MUTATION rather than a query even though it writes nothing: it is fired by an
 * explicit "Apply" click with a body, is never re-run on focus or mount, and its
 * result is transient checkout state — not a cache entry keyed by anything
 * meaningful.
 *
 * `silentError: true`: every failure is an expected, code-branched condition
 * (unknown code, expired, wrong plan, already redeemed) that earns specific inline
 * copy at the call site. Letting `MutationCache.onError` also fire would toast a
 * duplicate.
 */
export function useValidateCheckoutCodeMutation() {
  return useMutation<ValidateCheckoutCodeResponse, Error, { code: string; planId: number }>({
    meta: { silentError: true },
    mutationFn: (params) => validateCheckoutCode(params),
  });
}

/**
 * The org's billing changelog — an INFINITE query, because the endpoint is
 * keyset-paginated (`?cursor=<last id>`) rather than page-numbered.
 *
 * `getNextPageParam` reads the server's `nextCursor`, which is null on a partial
 * page; returning `undefined` there is what stops TanStack Query offering another
 * page. The cursor must come from the server rather than being derived from the
 * last item's id client-side — only the server knows whether a full page means
 * "more exist".
 *
 * Server-owned copy: `title`/`description` are stored at write time, so this is a
 * plain read with no client-side derivation.
 */
export function useChangelog(options?: { enabled?: boolean }) {
  return useInfiniteQuery<ChangelogResponse>({
    queryKey: billingKeys.changelog(),
    queryFn: ({ pageParam }) =>
      getChangelog({ limit: CHANGELOG_PAGE_SIZE, cursor: pageParam as number | null }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 60_000,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Checkout intent. The component owns inline + error-code-branched feedback
 * (PlanNotPurchasable → inline notice; otherwise a toast), so this stays
 * `silentError` to avoid a double toast. Not optimistic and no cache write:
 * the plan only changes once the Paddle webhook lands, which invalidates
 * `billingKeys.summary()` server-side via a later refetch.
 */
export function useCheckoutMutation() {
  return useMutation<CheckoutResponse, Error, number>({
    meta: { silentError: true },
    mutationFn: (planId: number) => createCheckout(planId),
  });
}

/**
 * Open the Paddle customer portal. Mints a session via
 * `POST /billing/customer-portal` and **navigates the tab to the returned URL in
 * `onSuccess`** — deliberately here rather than at the call site. Every consumer
 * of this hook wants exactly one thing (be taken to the portal), and the bug this
 * replaces was four separate render sites each describing a portal in prose while
 * none of them actually opened it. Owning the navigation in the hook makes the
 * dead end unrepresentable: you cannot call this and forget to go somewhere.
 *
 * See `lib/navigate-external.ts` for why this is a same-tab navigation rather
 * than `window.open` (popup blockers eat async `window.open`).
 *
 * There is no cache write or invalidation: nothing in our own state changes by
 * minting a portal session — any plan/payment change the user makes over on
 * Paddle comes back through the webhook, not this response.
 *
 * `silentError: true` because the callers own inline, error-code-branched UI:
 * `040501` is a permanent "no portal exists" state that must not be retried,
 * while everything else is transient and worth a toast. See
 * `isNoBillingPortal` in `plan-helpers.ts` and `BillingPortalButton`.
 */
export function useCustomerPortalMutation() {
  return useMutation<CustomerPortalResponse, Error, void>({
    mutationKey: billingKeys.customerPortal(),
    meta: { silentError: true },
    mutationFn: () => createCustomerPortalSession(),
    onSuccess: ({ portalUrl }) => navigateExternal(portalUrl),
  });
}

/**
 * Proration preview for an in-place upgrade to `planId` (P2). Dormant until a
 * target is chosen (`enabled` false / `planId` null). A price quote must never
 * be served stale, so `staleTime: 0`; and it must not silently re-quote when
 * the tab refocuses mid-confirm, so `refetchOnWindowFocus: false`. A preview
 * failure (`041006 PreviewFailed`) surfaces inline in the confirm dialog with a
 * retry, so `retry: false` (fail fast) + `silentError` (no double toast). The
 * same `silentError` covers the promo-code rejections a coded preview can fail
 * with (`Billing.Discount.*`) — those render as an inline FIELD error, so a
 * toast on top would double-report a typo.
 *
 * `code` is normalized (trim → `null` when blank) here rather than at the call
 * site so `"SAVE20"`, `" SAVE20 "`, `""`, and `undefined` can't fork the cache
 * into four entries for two distinct quotes.
 */
export function usePreviewUpgrade(
  planId: number | null,
  options?: { enabled?: boolean; code?: string | null },
) {
  const code = options?.code?.trim() || null;
  return useQuery<PreviewResponse>({
    queryKey: billingKeys.preview(planId ?? 0, code),
    queryFn: () => previewUpgrade(planId as number, code ?? undefined),
    enabled: (options?.enabled ?? true) && planId != null,
    staleTime: 0,
    gcTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    meta: { silentError: true },
  });
}

/**
 * Self-serve upgrade (P2). The change lands asynchronously via the Paddle
 * webhook, so there is no optimistic cache write: on success we invalidate the
 * whole `['billing']` root so subscription (polled by the card until the plan
 * flips), summary, and capabilities all refetch and converge on the new plan.
 * The card owns error-code-branched feedback (portal fallback / checkout
 * redirect / retry / a rejected promo code), so this stays `silentError` to
 * avoid a double toast.
 *
 * `code` is optional and applied as part of the same change, so it reduces the
 * prorated upgrade charge. It is sent only when non-empty (see
 * `upgradeSubscription`).
 */
export function useUpgradeMutation() {
  const queryClient = useQueryClient();
  return useMutation<UpgradeResponse, Error, { planId: number; code?: string }>({
    meta: { silentError: true },
    mutationFn: ({ planId, code }) => upgradeSubscription(planId, code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.all });
    },
  });
}

/**
 * Self-serve promo-code redemption (OWNER-only). Applies a discount to the org's
 * live Paddle subscription; like `useUpgradeMutation` the effect lands
 * **asynchronously** (Paddle applies it and it shows on the next invoice), so
 * there's no optimistic write. On success we invalidate `subscription` and
 * `summary` so any downstream state that reads the aggregate refetches — even
 * though today's payload doesn't change synchronously, a discount that lands
 * quickly (or a later background refetch) converges without a manual reload.
 *
 * `silentError: true` because the row owns inline, `errorCode`-branched feedback
 * (`redeemCodeErrorMessage` in `plan-helpers.ts`) — letting the `MutationCache`
 * pipe also fire would double-toast the same failure.
 */
export function useRedeemCodeMutation() {
  const queryClient = useQueryClient();
  return useMutation<RedeemCodeResponse, Error, string>({
    meta: { silentError: true },
    mutationFn: (code: string) => redeemCode(code),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
    },
  });
}

/**
 * One-time 7-day trial extension (OWNER-only). The write lands **synchronously
 * and locally** — a trial has no Paddle subscription to proxy to — so unlike
 * `useUpgradeMutation` there is nothing to poll for: the response IS the new
 * state. We invalidate `summary` (the app-wide banner reads `banner` from it,
 * and the backend stops emitting TRIAL_ENDING once the extension is spent) and
 * `subscription` (its `trialEndsAt`-derived expiry moves), and leave the rest of
 * the `['billing']` root alone — capabilities, plans, and invoices are unchanged
 * by a date shift.
 *
 * Success copy interpolates the new trial end, so it fires `notifySuccess`
 * inline rather than `meta.successMessage` — the xor rule in `query-client.ts`.
 * `silentError: true` because `041202 AlreadyExtended` is an expected state that
 * earns its own message at the call site; letting the `MutationCache.onError`
 * pipe also fire would double-toast it.
 */
export function useExtendTrialMutation() {
  const queryClient = useQueryClient();
  return useMutation<TrialExtensionResponse, Error, void>({
    meta: { silentError: true },
    mutationFn: () => extendTrial(),
    onSuccess: ({ trialEndsAt }) => {
      notifySuccess(`Trial extended. It now ends ${formatDate(toDate(trialEndsAt))}.`);
      void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
    },
  });
}

/**
 * Variables for `useLifecycleMutation`. `durationMonths` is a SEPARATE field
 * rather than extra `LifecycleAction` members (`'pause-3'`, `'pause-4'`, …)
 * because that union is by construction the URL segment each action POSTs to —
 * see `LifecycleAction` in `lib/billing.ts`. It is meaningful for **pause**
 * only; `null`/omitted books the indefinite pause, and `runLifecycleAction`
 * ignores it for the other three.
 */
export type LifecycleMutationVariables = {
  action: LifecycleAction;
  durationMonths?: PauseDurationMonths | null;
};

/**
 * Cancel / pause / resume / revoke-scheduled-change (OWNER-only). One hook for
 * all four: they share an endpoint contract and a response shape, and the action
 * travels in the mutation variable — so the caller holds one mutation object
 * whose `isPending` is scoped to whichever action it fired.
 *
 * These endpoints mutate NOTHING locally. They ask Paddle to act and return
 * `{ status: 'processing' }`; the `subscription.canceled/paused/resumed/updated`
 * webhook is the single writer of the resulting state. So the invalidate below is
 * a best-effort nudge, NOT the mechanism — the caller must poll for the change to
 * land (`useSubscription({ pollUntilStatus })` for a status flip,
 * `{ pollUntilNoScheduledChange }` for the revoke, both via
 * `useSubscriptionLifecycle`). Invalidating alone would show stale state until
 * the user refreshed.
 *
 * `silentError: true`: every failure here is an expected precondition
 * (`041101 NoPaddleSubscription`, `041102 NotActive`, `041103 NotPaused`,
 * `041105 ChangeAlreadyScheduled`, `041106 NoScheduledChange`,
 * `041107 PauseAnchorUnknown`) or a Paddle outage (`041104 RequestFailed`), each
 * of which earns specific copy at the call site. Letting `MutationCache.onError`
 * also fire would double-toast. Success copy is likewise interpolated per action
 * at the call site, so there is no `meta.successMessage` here either (the xor
 * rule in `query-client.ts`).
 */
export function useLifecycleMutation() {
  const queryClient = useQueryClient();
  return useMutation<LifecycleResponse, Error, LifecycleMutationVariables>({
    meta: { silentError: true },
    mutationFn: ({ action, durationMonths }) =>
      runLifecycleAction(action, durationMonths),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: billingKeys.subscription() });
      void queryClient.invalidateQueries({ queryKey: billingKeys.summary() });
      // Capabilities are status-keyed (`(subscriptionStatus, workspaceRole)`), so a
      // pause/resume genuinely changes the resolved set — unlike the trial
      // extension above, which only moves a date.
      void queryClient.invalidateQueries({ queryKey: billingKeys.capabilities() });
    },
  });
}
